import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import {
  brainApi,
  scopeOf,
  KNOWLEDGE_APPLIES_LABELS,
  KNOWLEDGE_APPLIES_SLUGS,
  knowledgeAppliesFromSlug,
  type KnowledgeAppliesTo,
  type KnowledgeRule,
} from '@/modules/brain/services/brain.api';
import { BrainScopeRail, type ScopeSelection, type ScopeWorkspace } from './BrainScopeRail';

const SCOPE_QUERY = 'scope';
const WS_QUERY = 'ws';

function readScope(params: URLSearchParams): ScopeSelection | null {
  const ws = params.get(WS_QUERY);
  if (ws) return { kind: 'workspace', workspaceId: ws };
  const scope = params.get(SCOPE_QUERY);
  if (scope === 'org') return { kind: 'org' };
  if (scope === 'personal') return { kind: 'personal' };
  return null;
}

function writeScope(params: URLSearchParams, scope: ScopeSelection): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(SCOPE_QUERY);
  next.delete(WS_QUERY);
  if (scope.kind === 'org') next.set(SCOPE_QUERY, 'org');
  else if (scope.kind === 'personal') next.set(SCOPE_QUERY, 'personal');
  else if (scope.kind === 'workspace') next.set(WS_QUERY, scope.workspaceId);
  return next;
}

export function KnowledgeAppliesPage() {
  const { applies: rawApplies } = useParams<{ applies: string }>();
  const applies = knowledgeAppliesFromSlug(rawApplies);

  if (!applies) {
    return <Navigate to={`/brain/knowledge/${KNOWLEDGE_APPLIES_SLUGS.global}`} replace />;
  }

  return <KnowledgeAppliesView applies={applies} />;
}

function KnowledgeAppliesView({ applies }: { applies: KnowledgeAppliesTo }) {
  const { hasPermission, currentWorkspaceId, workspaces } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const canManageOrg = hasPermission('knowledge.org.manage');
  const canManageWorkspace = hasPermission('knowledge.workspace.manage');
  const canViewOrg = hasPermission('knowledge.org.view') || canManageOrg;
  const canViewWorkspace = hasPermission('knowledge.workspace.view') || canManageWorkspace;

  const accessibleWorkspaces: ScopeWorkspace[] = useMemo(() => {
    if (!canViewWorkspace) return [];
    return workspaces.map((ws) => ({
      _id: ws._id,
      name: ws.name,
      current: ws._id === currentWorkspaceId,
    }));
  }, [canViewWorkspace, workspaces, currentWorkspaceId]);

  const defaultScope: ScopeSelection = { kind: 'personal' };
  const scope = readScope(searchParams) ?? defaultScope;
  const setScope = (next: ScopeSelection) => setSearchParams(writeScope(searchParams, next));

  const [rules, setRules] = useState<KnowledgeRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTarget, setEditorTarget] = useState<KnowledgeRule | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await brainApi.listKnowledgeRules({ appliesTo: applies });
      setRules(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load knowledge rules';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [applies]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const visibleItems = useMemo(() => {
    return rules.filter((r) => {
      const s = scopeOf(r);
      if (scope.kind === 'personal') return s === 'personal';
      if (scope.kind === 'org') return s === 'org';
      if (scope.kind === 'workspace') return r.workspaceId === scope.workspaceId;
      return false;
    });
  }, [rules, scope]);

  // Group by genre within the active scope.
  const grouped = useMemo(() => {
    const map = new Map<string, KnowledgeRule[]>();
    for (const r of visibleItems) {
      const key = (r.genre || '').trim() || 'Uncategorized';
      const list = map.get(key) || [];
      list.push(r);
      map.set(key, list);
    }
    const entries = Array.from(map.entries());
    entries.sort(([a], [b]) => {
      if (a === 'Uncategorized') return 1;
      if (b === 'Uncategorized') return -1;
      return a.localeCompare(b);
    });
    return entries.map(([genre, items]) => ({ genre, items }));
  }, [visibleItems]);

  // Members (no manage perms anywhere) can browse Brain in read-only mode.
  // Personal scope also requires at least one manage capability so a Member
  // can't slip in via the personal tab.
  const canManageAny = canManageOrg || canManageWorkspace;
  const canManageHere = (() => {
    if (scope.kind === 'personal') return canManageAny;
    if (scope.kind === 'org') return canManageOrg;
    if (scope.kind === 'workspace') return canManageWorkspace;
    return false;
  })();

  const scopeLabel = (() => {
    if (scope.kind === 'personal') return 'Personal';
    if (scope.kind === 'org') return 'Organization';
    if (scope.kind === 'workspace') {
      return accessibleWorkspaces.find((w) => w._id === scope.workspaceId)?.name || 'Workspace';
    }
    return '';
  })();

  const scopeBlurb = (() => {
    if (scope.kind === 'personal') return 'Rules only visible to you.';
    if (scope.kind === 'org') return 'Rules shared across your organization.';
    if (scope.kind === 'workspace') return 'Rules scoped to this workspace.';
    return '';
  })();

  const appliesLabel = KNOWLEDGE_APPLIES_LABELS[applies];

  const handleSave = async (data: {
    name: string;
    trigger: string;
    instruction: string;
    genre: string;
    isActive: boolean;
  }) => {
    try {
      if (editorTarget) {
        const { data: updated } = await brainApi.updateKnowledgeRule(editorTarget._id, {
          ...data,
          appliesTo: applies,
        });
        setRules((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
      } else {
        const { data: created } = await brainApi.createKnowledgeRule({
          ...data,
          appliesTo: applies,
          scope: scope.kind,
          workspaceId: scope.kind === 'workspace' ? scope.workspaceId : undefined,
        });
        setRules((prev) => [...prev, created]);
      }
      setEditorOpen(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save knowledge rule';
      setError(message);
    }
  };

  const handleDelete = async (rule: KnowledgeRule) => {
    if (!confirm(`Delete "${rule.name}"?`)) return;
    const backup = rules;
    setRules((prev) => prev.filter((r) => r._id !== rule._id));
    try {
      await brainApi.deleteKnowledgeRule(rule._id);
    } catch (err: unknown) {
      setRules(backup);
      const message = err instanceof Error ? err.message : 'Failed to delete';
      setError(message);
    }
  };

  const handleToggleActive = async (rule: KnowledgeRule) => {
    const next = !rule.isActive;
    setRules((prev) => prev.map((r) => (r._id === rule._id ? { ...r, isActive: next } : r)));
    try {
      await brainApi.updateKnowledgeRule(rule._id, { isActive: next });
    } catch (err: unknown) {
      setRules((prev) =>
        prev.map((r) => (r._id === rule._id ? { ...r, isActive: rule.isActive } : r)),
      );
      const message = err instanceof Error ? err.message : 'Failed to toggle';
      setError(message);
    }
  };

  return (
    <div className="brain-page">
      <BrainScopeRail
        title={appliesLabel}
        subtitle={`Knowledge for ${appliesLabel}`}
        current={scope}
        showPersonal
        showOrg={canViewOrg}
        workspaces={accessibleWorkspaces}
        onSelect={setScope}
      />

      <section className="brain-content">
        <header className="brain-content-header">
          <div>
            <h1>{scopeLabel}</h1>
            <p>{scopeBlurb}</p>
          </div>
          {canManageHere && (
            <button
              type="button"
              className="brain-primary-button"
              onClick={() => {
                setEditorTarget(null);
                setEditorOpen(true);
              }}
            >
              <Plus size={14} strokeWidth={1.7} />
              <span>New rule</span>
            </button>
          )}
        </header>

        {error && (
          <div className="brain-error">
            <span>{error}</span>
            <button onClick={() => setError(null)}>dismiss</button>
          </div>
        )}

        {loading ? (
          <div className="brain-loading">Loading…</div>
        ) : grouped.length === 0 ? (
          <div className="brain-empty">
            <p>No {appliesLabel} rules in {scopeLabel} yet.</p>
            {canManageHere && (
              <button
                type="button"
                className="brain-primary-button"
                onClick={() => {
                  setEditorTarget(null);
                  setEditorOpen(true);
                }}
              >
                <Plus size={14} strokeWidth={1.7} />
                <span>Create the first one</span>
              </button>
            )}
          </div>
        ) : (
          <div className="brain-groups">
            {grouped.map(({ genre, items }) => (
              <section key={genre} className="brain-group">
                <header className="brain-group-header">
                  <h3>{genre}</h3>
                  <span className="brain-group-count">{items.length}</span>
                </header>
                <ul className="brain-group-list">
                  {items.map((r) => (
                    <li key={r._id} className="brain-row">
                      <div className="brain-row-main">
                        <div className="brain-row-title">
                          <span>{r.name}</span>
                          {!r.isActive && <span className="brain-row-tag muted">Disabled</span>}
                        </div>
                        {r.trigger && (
                          <p className="brain-row-description">
                            <span className="brain-row-description-label">When </span>
                            {r.trigger}
                          </p>
                        )}
                        {r.instruction && (
                          <p className="brain-row-description">{r.instruction}</p>
                        )}
                      </div>
                      {canManageHere && (
                        <div className="brain-row-actions">
                          <button type="button" onClick={() => handleToggleActive(r)}>
                            {r.isActive ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditorTarget(r);
                              setEditorOpen(true);
                            }}
                          >
                            Edit
                          </button>
                          <button type="button" className="danger" onClick={() => handleDelete(r)}>
                            Delete
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </section>

      {editorOpen && (
        <KnowledgeEditor
          existing={editorTarget}
          appliesLabel={appliesLabel}
          scopeLabel={scopeLabel}
          onClose={() => setEditorOpen(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function KnowledgeEditor({
  existing,
  appliesLabel,
  scopeLabel,
  onClose,
  onSave,
}: {
  existing: KnowledgeRule | null;
  appliesLabel: string;
  scopeLabel: string;
  onClose: () => void;
  onSave: (data: {
    name: string;
    trigger: string;
    instruction: string;
    genre: string;
    isActive: boolean;
  }) => void;
}) {
  const [name, setName] = useState(existing?.name || '');
  const [trigger, setTrigger] = useState(existing?.trigger || '');
  const [instruction, setInstruction] = useState(existing?.instruction || '');
  const [genre, setGenre] = useState(existing?.genre || '');
  const [isActive, setIsActive] = useState(existing?.isActive ?? true);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !instruction.trim()) return;
    onSave({
      name: name.trim(),
      trigger: trigger.trim(),
      instruction: instruction.trim(),
      genre: genre.trim(),
      isActive,
    });
  };

  return (
    <div className="brain-modal-backdrop">
      <form onSubmit={submit} className="brain-modal">
        <header>
          <h2>{existing ? 'Edit rule' : 'New rule'}</h2>
          <p>
            {appliesLabel} · {scopeLabel}
          </p>
        </header>
        <label className="brain-field">
          <span>Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            placeholder="e.g. Software engineer pipeline"
          />
        </label>
        <label className="brain-field">
          <span>Genre</span>
          <input
            value={genre}
            onChange={(e) => setGenre(e.target.value)}
            placeholder="e.g. hiring rubric, tone of voice"
          />
        </label>
        <label className="brain-field">
          <span>Trigger (when…)</span>
          <textarea
            value={trigger}
            onChange={(e) => setTrigger(e.target.value)}
            rows={2}
            placeholder="When the role is for a software engineer"
          />
        </label>
        <label className="brain-field">
          <span>Instruction</span>
          <textarea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            rows={4}
            required
            placeholder="Use the engineering pipeline preset and require a system-design round."
          />
        </label>
        <label className="brain-field-inline">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          <span>Active</span>
        </label>
        <footer>
          <button type="button" className="brain-secondary-button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="brain-primary-button">
            {existing ? 'Save changes' : 'Create rule'}
          </button>
        </footer>
      </form>
    </div>
  );
}
